import {createSelector} from 'reselect';
import {OrderedMap} from 'immutable';
import {UserRecord, type UserRecordType} from '@packages/systems/designer/user';
import {SITE_ROLES} from '@packages/domains/authorization/permissions/catalog';
import type {DesignerStores} from '@packages/systems/designer/flux';

export function reifyUser(raw: Partial<UserRecordType>) {
  return new UserRecord(raw);
}

export function reifyUsers(raw: Partial<UserRecordType>[]) {
  return OrderedMap(raw.map((c) => [c._id, reifyUser(c)]));
}

export function stripPrefix(id: string) {
  return (
    id &&
    id
      .replace(/^Person_/, '')
      .replace(/^Collaborator_/, '')
      .replace(/^Flow_/, '')
  );
}

export const fullName = ({firstName, lastName}: UserRecordType) =>
  firstName && lastName
    ? `${firstName} ${lastName}`
    : firstName || lastName || '';

export const isCommenter = (user: UserRecordType) =>
  user?.roles?.site === SITE_ROLES.commenter;

export const displayName = (
  user: UserRecordType | {firstName: string; lastName: string; email: string}
) => fullName(user as UserRecordType) || (user as {email: string}).email || '';

const firstLetter = (str: string | null) => (str ? str.charAt(0) : '');

export const initials = ({firstName}: UserRecordType) => firstLetter(firstName);

export const profileImage = (user: UserRecordType) =>
  (user.profileImg as string) || gravatar(user);

export const tooltipName = (user: UserRecordType = {} as UserRecordType) =>
  user.firstName ? `${user.firstName} ${user?.lastName || ''}` : 'Unknown user';

const gravatar = ({emailHash}: UserRecordType) =>
  `https://secure.gravatar.com/avatar/${emailHash}?d=404`;

export const selectCurrentUserRole = createSelector(
  (state: DesignerStores) => state.UserStore.siteDesigners,
  (state: DesignerStores) => state.SiteDataStore.userId,
  (users, userId) => {
    const user = users.get(stripPrefix(userId));
    if (user == null) {
      return null;
    }

    return user.roles?.site || null;
  }
);

export const selectCurrentUserWorkspaceRole = createSelector(
  (state: DesignerStores) => state.UserStore.siteUsers,
  (state: DesignerStores) => state.SiteDataStore.userId,
  (users, userId) => {
    const user = users.get(stripPrefix(userId));
    if (user == null) {
      return null;
    }

    return user.roles?.workspace || null;
  }
);
